/**
 * 案件モードの左右比較の開閉を、URL（`?compare=0`）に持つ。
 *
 * **開閉を画面の状態に閉じない。** 画面の状態に置くと、閉じた姿を共有した相手には開いた姿が
 * 届き、再読み込みのたびに開閉が戻る。案件モードは既定で左右 2 面、閉じたときだけ URL に
 * `compare=0` を書く（読み書きの規則は `flow-search.ts`）。
 *
 * **差が無ければ URL に関係なく 1 面にする**（2026-09-29 に決めた）。変更前と変更後が同じなら、
 * 左右に並べても同じ姿が 2 つ出るだけで、読み手は無い違いを探すことになる。既定 2 面と
 * `compare=0` の規則は、差が有るときだけ効く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件モードの左右比較の開閉を、URL の検索条件から読み、押したら書き換え、差が無ければ 1 面に固定する。
 * @graph-connects isCompareOpen [calls] 検索条件から開閉を読む
 * @graph-connects withCompare [calls] 開閉だけを差し替えた検索条件
 */
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback } from 'react';

import { isCompareOpen, withCompare } from '../flow-search.js';

/**
 * `useCompareSearch` が返すもの。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 左右比較を開いているか・切り替えを押せるかと、押したときに切り替える口を表す。
 * @graph-connects none
 */
export interface CompareSearch {
  /** 左右 2 面で出しているか。差が無ければ URL に関係なく false。 */
  split: boolean;
  /** 差が無く、切り替えのボタンを押せないか。 */
  disabled: boolean;
  /** 押したときに開閉を切り替える。 */
  toggle: () => void;
}

/**
 * 今の URL から左右比較の開閉を読み、切り替えの口を返す。
 *
 * 切り替えは履歴を積まずに置き換える（`replace`）。開閉は見え方の切り替えで、戻る操作で
 * 1 つ前の開閉へ戻ると、読み手は前の画面へ戻ったつもりで同じ画面に留まる。
 *
 * 差が無いときは URL の開閉に関係なく、1 面と押せない状態を返す。
 *
 * @param hasDiff 変更前と変更後に差が有るか。画面が自分の突き合わせの結果から渡す
 * @returns 開閉・押せるかと切り替えの口
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件モードの左右比較の開閉を URL から読み、差が無ければ 1 面で押せない状態を返し、押したら URL の開閉だけを書き換える。
 * @graph-connects isCompareOpen [calls] 検索条件から開閉を読む
 * @graph-connects withCompare [calls] 開閉だけを差し替えた検索条件
 */
export function useCompareSearch(hasDiff: boolean): CompareSearch {
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const split = hasDiff && isCompareOpen(search);

  const toggle = useCallback(() => {
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => withCompare(prev, !split),
      replace: true,
    });
  }, [navigate, split]);

  return { split, disabled: !hasDiff, toggle };
}
